import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { MobileModal } from 'components/UI/Modal/MobileModal'
import { NoteManager } from 'components/Notes/NoteManager'
import SearchPage from 'pages/app/mobile/search'
import useApiService from 'services/api'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { FbLink } from 'components/FbUI/FbLink'
import { MobileColors } from 'styles/mobileTokens'
import { WizardHeader, WizardFooter } from 'components/Mobile/wizard'

type FormValues = {
  note: string
  attachments: File[]
}

// Two-step wizard: write the note first, then scope it to a company/door.
const STEPS = ['note', 'company'] as const

// Quill editor overrides for the mobile note editor. Kept as a styled-component
// (rather than Tailwind) because these target Quill's internal `.ql-*` classes —
// third-party DOM we don't own — which utility classes can't reach cleanly.
const EditorContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 16px;

  /* NoteManager/Editor wrap the Quill editor in a few layout-only <div>s.
     Collapse just that chain (not the attachments sibling) with
     display: contents so the editor and its toolbar become direct flex
     children of this column — the writing area then grows to fill the height
     down to the footer, matching the create-note mockup, instead of
     collapsing to its min-height. */
  > div,
  > div > .mt-2,
  > div > .mt-2 > div {
    display: contents;
  }

  .mobile-note-editor {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    padding: 12px;
  }

  .ql-container {
    flex: 1;
    min-height: 0;
    border: none;
    font-size: 16px;
  }

  .ql-editor {
    flex: 1;
    padding: 0;
    overflow-y: auto;
    min-height: 200px;
  }

  .ql-editor.ql-blank::before {
    color: ${MobileColors.textSecondary};
    font-style: normal;
  }

  /* Toolbar keeps its height at the bottom of the column, above the footer. */
  .ql-toolbar {
    flex-shrink: 0;
    background: white;
    border: none;
    border-top: 1px solid ${MobileColors.borderDefault};
    padding: 8px 0;
  }
`

export function MobileCreateNotePage() {
  const navigate = useNavigate()
  const api = useApiService()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  // Check for pre-selected chain from query param
  const preselectedChainId = searchParams.get('chain_id')
    ? Number(searchParams.get('chain_id'))
    : undefined

  const [stepIndex, setStepIndex] = useState(0)
  const step = STEPS[stepIndex]
  const stepNum = stepIndex + 1
  const stepCount = STEPS.length

  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()

  // Determine if the selected result is a door or company
  const isDoorSelected =
    selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  // Get chain data for company selection
  const chainId = !isDoorSelected
    ? Number(selectedSearchResult?.item_id) || preselectedChainId
    : undefined
  const { data: chainData } = api.useGetChain(chainId)

  // Get door data for door selection
  const restaurantId = isDoorSelected
    ? String((selectedSearchResult as DoorSearchResult).id)
    : undefined
  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  // Get the contact_company_id and door_proxy_id
  const contactCompanyId = isDoorSelected
    ? doorProxy?.chain_proxy?.id
    : chainData?.contact_company_id

  const doorProxyId = isDoorSelected ? doorProxy?.id : undefined

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      note: '',
      attachments: [],
    },
  })

  const noteContent = watch('note')
  const hasContent = noteContent && noteContent.trim().length > 0

  // Mutation for creating note
  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!contactCompanyId) {
        throw new Error('No company selected')
      }

      return await api.createNote(
        chainId,
        contactCompanyId,
        values.note,
        values.attachments,
        doorProxyId
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes-infinite'] })
      toast.success('Note created successfully')
      navigate(-1)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create note')
    },
  })

  const isSubmitting = mutation.isPending

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  const handleClick = (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => {
    setSelectedSearchResult(result)
  }

  // A deep-linked note (?chain_id=) is already scoped: `chainId` falls back to
  // `preselectedChainId`, so `contactCompanyId` resolves and the company step is
  // ready to submit even before the user touches search — no effect needed to
  // seed selection. The name is derived from the fetched chain below.
  const companyDisplay = isDoorSelected
    ? doorProxy?.chain_proxy?.name || ''
    : selectedSearchResult?.name || chainData?.chain || ''

  const doorDisplay = isDoorSelected
    ? (selectedSearchResult as DoorSearchResult)?.name ||
      doorProxy?.restaurant?.restaurant_name ||
      ''
    : ''

  const handleClose = () => {
    navigate(-1)
  }

  // Step 0 exits the flow; later steps walk back one screen. Note content is
  // preserved when stepping back from the company step.
  const goBack = () => {
    if (stepIndex === 0) {
      navigate(-1)
    } else {
      setStepIndex((i) => i - 1)
    }
  }

  const handleNextFromNote = () => {
    if (!hasContent) return
    setStepIndex(1)
  }

  const renderFooter = () => {
    if (step === 'note') {
      return (
        <WizardFooter.Root>
          <WizardFooter.Next
            onClick={handleNextFromNote}
            disabled={!hasContent}
          >
            Next
          </WizardFooter.Next>
        </WizardFooter.Root>
      )
    }
    return (
      <WizardFooter.Root>
        <WizardFooter.Back onClick={goBack} disabled={isSubmitting} />
        <WizardFooter.Next
          onClick={onSubmit}
          disabled={!contactCompanyId || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Note'}
        </WizardFooter.Next>
      </WizardFooter.Root>
    )
  }

  return (
    <MobileModal
      fullScreen
      onCancel={handleClose}
      header={
        <WizardHeader
          eyebrow="Create a note"
          title={step === 'note' ? 'Add a new note' : 'Choose a company'}
          stepNum={stepNum}
          stepCount={stepCount}
        >
          {step === 'company' && contactCompanyId && (
            <div className="flex items-center gap-2 text-sm mt-1">
              <FbLink to={`/chain/${chainId ?? contactCompanyId}`}>
                {companyDisplay}
              </FbLink>
              {isDoorSelected && doorProxyId && (
                <>
                  <span className="text-fg-tertiary">/</span>
                  <FbLink to={`/restaurant/${doorProxyId}`}>
                    {doorDisplay}
                  </FbLink>
                </>
              )}
            </div>
          )}
        </WizardHeader>
      }
      footer={renderFooter()}
    >
      {step === 'note' ? (
        <EditorContainer>
          <NoteManager<FormValues>
            control={control}
            noteName="note"
            attachmentsName="attachments"
            label=""
            placeholder="Start writing note here"
            viewMode={false}
            editorClassName="mobile-note-editor"
            enableMentions
            disabled={isSubmitting}
          />
        </EditorContainer>
      ) : (
        <div className="w-full">
          <SearchPage
            hideBack
            onClick={handleClick}
            selectedItemId={selectedSearchResult?.item_id ?? preselectedChainId}
          />
        </div>
      )}
    </MobileModal>
  )
}
